// Masking puramente visual no cliente. NUNCA usar eval/Function.
// Regras vêm da config do inbox; presets têm replacement fixo e seguro.

const MAX_PATTERN_LENGTH = 200;
// Guard O(1) contra ReDoS: textos muito longos nao sao processados por regex.
const MAX_TEXT_LENGTH = 10000;
const ALLOWED_FLAGS = ['g', 'i', 'm', 'u'];

// Mantém os N últimos dígitos visíveis, mascara o resto preservando separadores.
const maskKeepLast = (match, lastN, maskChar = '•') => {
  const digits = match.replace(/\D/g, '');
  if (digits.length <= lastN) return match;
  const visible = digits.slice(-lastN);
  const maskedDigits = maskChar.repeat(digits.length - lastN) + visible;
  let i = 0;
  // reaplica a máscara respeitando a posição dos dígitos originais (mantém espaços/traços)
  return match.replace(/\d/g, () => maskedDigits[i++]);
};

// Algoritmo de Luhn - reduz falso-positivo do preset de cartão.
const isLuhnValid = value => {
  const digits = value.replace(/\D/g, '');
  if (digits.length < 13 || digits.length > 19) return false;
  let sum = 0;
  let alt = false;
  for (let i = digits.length - 1; i >= 0; i -= 1) {
    let n = Number(digits[i]);
    if (alt) {
      n *= 2;
      if (n > 9) n -= 9;
    }
    sum += n;
    alt = !alt;
  }
  return sum % 10 === 0;
};

// Catálogo de presets. regex SEMPRE com flag global.
export const MASKING_PRESETS = {
  credit_card: {
    id: 'credit_card',
    labelKey: 'INBOX_MGMT.MASKING.PRESETS.CREDIT_CARD',
    regex: /\b(?:\d[ -]?){13,19}\b/g,
    replace: match => (isLuhnValid(match) ? maskKeepLast(match, 4) : match),
  },
  cpf: {
    id: 'cpf',
    labelKey: 'INBOX_MGMT.MASKING.PRESETS.CPF',
    regex: /\b\d{3}\.?\d{3}\.?\d{3}-?\d{2}\b/g,
    replace: () => '***.***.***-**',
  },
  cnpj: {
    id: 'cnpj',
    labelKey: 'INBOX_MGMT.MASKING.PRESETS.CNPJ',
    regex: /\b\d{2}\.?\d{3}\.?\d{3}\/?\d{4}-?\d{2}\b/g,
    replace: () => '**.***.***/****-**',
  },
  pin_4: {
    id: 'pin_4',
    labelKey: 'INBOX_MGMT.MASKING.PRESETS.PIN_4',
    // alto risco de falso-positivo: opt-in explicito por inbox.
    regex: /\b\d{4}\b/g,
    replace: () => '••••',
  },
};

const sanitizeFlags = flags => {
  const requested = (flags || '').split('');
  const safe = requested.filter(f => ALLOWED_FLAGS.includes(f));
  if (!safe.includes('g')) safe.push('g');
  return Array.from(new Set(safe)).join('');
};

// Constroi a lista de regras compiladas a partir da config do inbox.
// Regra invalida e descartada (nunca quebra o render).
const buildCompiledRules = rules => {
  const compiled = [];
  (rules || []).forEach(rule => {
    if (rule.enabled === false) return;

    if (rule.type === 'preset') {
      const preset = MASKING_PRESETS[rule.preset];
      if (preset) {
        compiled.push({
          regex: new RegExp(preset.regex.source, 'g'),
          replace: preset.replace,
        });
      }
      return;
    }

    if (rule.type === 'regex' && rule.pattern) {
      if (rule.pattern.length > MAX_PATTERN_LENGTH) return;
      if (typeof rule.replacement !== 'string' || /[<>]/.test(rule.replacement))
        return;
      try {
        const regex = new RegExp(rule.pattern, sanitizeFlags(rule.flags));
        compiled.push({ regex, replace: rule.replacement }); // replacement string ($1..$9 nativo)
      } catch {
        // regex invalida -> descarta silenciosamente
      }
    }
  });
  return compiled;
};

// Cache de compilacao por hash das regras (evita recompilar RegExp por item de lista).
const compileCache = new Map();
const COMPILE_CACHE_LIMIT = 50;

export const compileMaskingRules = rules => {
  const key = JSON.stringify(rules || []);
  if (compileCache.has(key)) return compileCache.get(key);
  const compiled = buildCompiledRules(rules);
  if (compileCache.size >= COMPILE_CACHE_LIMIT) {
    const oldest = compileCache.keys().next().value;
    compileCache.delete(oldest);
  }
  compileCache.set(key, compiled);
  return compiled;
};

// Cache LRU simples (resultado mascarado) para previews/listas grandes.
const resultCache = new Map();
const RESULT_CACHE_LIMIT = 500;

export const applyMasking = (text, compiledRules) => {
  if (!text || !compiledRules || compiledRules.length === 0) return text;

  let output = text;
  compiledRules.forEach(({ regex, replace }) => {
    regex.lastIndex = 0;
    output = output.replace(regex, replace);
  });
  return output;
};

// Ponto de entrada memoizado: recebe texto + regras (cruas do inbox) -> texto mascarado.
export const maskText = (text, rules) => {
  if (!text) return text;
  const compiled = compileMaskingRules(rules);
  if (compiled.length === 0) return text;
  // Curto-circuito O(1): textos muito longos nao sao mascarados (mitiga ReDoS).
  if (text.length > MAX_TEXT_LENGTH) return text;

  const cacheKey = `${JSON.stringify(rules)} ${text}`;
  if (resultCache.has(cacheKey)) return resultCache.get(cacheKey);

  const masked = applyMasking(text, compiled);
  if (resultCache.size >= RESULT_CACHE_LIMIT) {
    const oldest = resultCache.keys().next().value;
    resultCache.delete(oldest);
  }
  resultCache.set(cacheKey, masked);
  return masked;
};
